import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Container from './Container';

const drag = (element, callback) => {
  let state = 'stopped';
  let x;
  let y;
  let originalX;
  let originalY;
  let totalDistanceX;
  let totalDistanceY;

  const trigger = event => {
    const { touches, cancelable } = event;
    const { clientX, clientY } = touches ? touches[0] : event;
    const stepX = clientX - x;
    const stepY = clientY - y;
    x = clientX;
    y = clientY;
    totalDistanceX += Math.abs(stepX);
    totalDistanceY += Math.abs(stepY);

    if (cancelable) {
      event.preventDefault();
    }

    callback({
      state,
      element,
      event,
      x,
      y,
      originalX,
      originalY,
      stepX,
      stepY,
      distanceX: x - originalX,
      distanceY: y - originalY,
      totalDistanceX,
      totalDistanceY,
    });
  };

  const move = event => {
    state = 'dragging';
    trigger(event);
  };

  const stop = () => {
    if (state !== 'stopped') {
      state = 'stopped';
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', stop);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
      trigger({
        clientX: x,
        clientY: y,
        preventDefault: () => {},
        stopPropagation: () => {},
      });
    }
  };

  const start = event => {
    const { clientX, clientY } = event.touches ? event.touches[0] : event;

    if (state === 'stopped') {
      state = 'starting';
      originalX = clientX;
      originalY = clientY;
      x = clientX;
      y = clientY;
      totalDistanceX = 0;
      totalDistanceY = 0;

      document.addEventListener('touchmove', move, { passive: false });
      document.addEventListener('touchend', stop, { passive: false });
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', stop);
      trigger(event);
    }
  };

  element.addEventListener('touchstart', start, { passive: false });
  element.addEventListener('mousedown', start);

  return () => {
    stop();
    element.removeEventListener('mousedown', start);
  };
};

const DragHandle = ({ children, onDrag, ...props }) => {
  const ref = useRef();

  useEffect(() => drag(ref.current, onDrag), [ref, onDrag]);

  return (
    <Container ref={ref} {...props}>
      {children}
    </Container>
  );
};

DragHandle.propTypes = {
  onDrag: PropTypes.func.isRequired,
  children: PropTypes.node,
};

DragHandle.defaultProps = {
  children: null,
};

export default DragHandle;
